/**
 * SIT v1 Test Plan — JSON Schema and a canonical example.
 *
 * The shape here is authoritative; both the frontend editor (when Ajv is wired up)
 * and the backend Runner Lambda validate against this schema before any outbound
 * call is placed.
 */

const matcherSchema = {
  oneOf: [
    {
      type: 'object',
      additionalProperties: false,
      properties: { substring: { type: 'string', minLength: 1 } },
      required: ['substring']
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: { regex: { type: 'string', minLength: 1 } },
      required: ['regex']
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        semantic: {
          type: 'object',
          additionalProperties: false,
          properties: {
            intent: { type: 'string', minLength: 1 },
            tolerance: { type: 'string', enum: ['low', 'medium', 'high'] }
          },
          required: ['intent']
        }
      },
      required: ['semantic']
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        any: { type: 'array', minItems: 1, items: { $ref: '#/definitions/matcher' } }
      },
      required: ['any']
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: {
        all: { type: 'array', minItems: 1, items: { $ref: '#/definitions/matcher' } }
      },
      required: ['all']
    },
    {
      type: 'object',
      additionalProperties: false,
      properties: { not: { $ref: '#/definitions/matcher' } },
      required: ['not']
    }
  ]
};

const e164 = { type: 'string', pattern: '^\\+[1-9][0-9]{6,14}$' };

export const TEST_PLAN_SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  $id: 'https://presidio-sit/test-plan-v1.json',
  title: 'SIT Test Plan v1',
  type: 'object',
  additionalProperties: false,
  required: ['apiVersion', 'kind', 'metadata', 'target', 'steps'],
  properties: {
    apiVersion: { const: 'presidio-sit/v1' },
    kind: { const: 'TestPlan' },
    metadata: {
      type: 'object',
      additionalProperties: false,
      required: ['id', 'name'],
      properties: {
        id: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{2,63}$' },
        name: { type: 'string', minLength: 1 },
        owner: { type: 'string', format: 'email' },
        tags: { type: 'array', items: { type: 'string' } },
        // Free-form notes captured on the Verify-Plans screen. Surfaced in the
        // run report so reviewers see WHY this plan exists / what to watch for.
        notes: { type: 'string', maxLength: 5000 },
        // Manual IVR setup the engineer must do before this plan can pass.
        // Examples: "Holiday flag = ON", "After-hours mode = ON", "MOTD enabled".
        // The SPA badges plans with preconditions + shows a pre-run modal that
        // dedupes setup steps across the batch.
        preconditions: {
          type: 'array',
          maxItems: 10,
          items: {
            type: 'object',
            additionalProperties: false,
            required: ['label', 'value'],
            properties: {
              label: { type: 'string', minLength: 1, maxLength: 80 },
              value: { type: 'string', minLength: 1, maxLength: 80 },
              action: { type: 'string', maxLength: 300 }
            }
          }
        }
      }
    },
    target: {
      type: 'object',
      additionalProperties: false,
      required: ['ccaas', 'destination', 'fromDid'],
      properties: {
        ccaas: { type: 'string', enum: ['wxcc', 'aws-connect', 'five9', 'genesys'] },
        destination: e164,
        fromDid: e164
      }
    },
    execution: {
      type: 'object',
      additionalProperties: false,
      properties: {
        maxDurationSec: { type: 'integer', minimum: 10, maximum: 1800 },
        recordAudio: { type: 'boolean' },
        ttsVoice: { type: 'string' },
        sttProvider: { type: 'string', enum: ['aws-transcribe', 'deepgram'] },
        language: { type: 'string' }
      }
    },
    steps: {
      type: 'array',
      minItems: 1,
      items: { $ref: '#/definitions/step' }
    },
    postRun: {
      type: 'object',
      additionalProperties: false,
      properties: {
        fetchCcaasLogs: { type: 'boolean' },
        correlationStrategy: {
          type: 'object',
          additionalProperties: false,
          properties: {
            ani: { type: 'string' },
            timeWindowSec: { type: 'integer', minimum: 10, maximum: 3600 }
          }
        }
      }
    }
  },
  definitions: {
    matcher: matcherSchema,
    step: {
      type: 'object',
      additionalProperties: false,
      required: ['id'],
      properties: {
        id: { type: 'string', pattern: '^[a-z0-9][a-z0-9-]{0,63}$' },
        onFailure: { type: 'string', enum: ['continue', 'abort'] },
        expectPrompt: {
          type: 'object',
          additionalProperties: false,
          required: ['match'],
          properties: {
            timeoutSec: { type: 'integer', minimum: 1, maximum: 120 },
            language: { type: 'string' },
            match: { $ref: '#/definitions/matcher' }
          }
        },
        pressDtmf: {
          type: 'object',
          additionalProperties: false,
          required: ['digits'],
          properties: {
            digits: { type: 'string', pattern: '^[0-9*#A-D]{1,32}$' },
            signaling: { type: 'string', enum: ['rfc2833', 'inband'] },
            postDelayMs: { type: 'integer', minimum: 0, maximum: 10000 }
          }
        },
        saySpeech: {
          type: 'object',
          additionalProperties: false,
          required: ['text'],
          properties: {
            text: { type: 'string', minLength: 1 },
            voice: { type: 'string' }
          }
        },
        expectTransfer: {
          type: 'object',
          additionalProperties: false,
          properties: {
            timeoutSec: { type: 'integer', minimum: 1, maximum: 300 },
            to: {
              type: 'object',
              additionalProperties: false,
              properties: {
                queueName: { type: 'string' },
                agentId: { type: 'string' }
              }
            },
            allowPromptTextEvidence: { type: 'boolean' }
          }
        },
        hangup: { type: 'object', additionalProperties: false }
      }
    }
  }
};

export const EXAMPLE_PLAN_YAML = `apiVersion: presidio-sit/v1
kind: TestPlan
metadata:
  id: wxcc-main-ivr-spanish-billing
  name: "WxCC Main IVR — Spanish billing path"
  owner: paul@presidio.com
  tags: [wxcc, ivr, spanish, regression]

target:
  ccaas: wxcc
  destination: "+15551234567"
  fromDid: "+15557654321"

execution:
  maxDurationSec: 180
  recordAudio: true
  ttsVoice: Joanna
  sttProvider: aws-transcribe
  language: en-US

steps:
  - id: greeting
    expectPrompt:
      timeoutSec: 15
      match:
        any:
          - substring: "thank you for calling"
          - semantic: { intent: "company greeting", tolerance: medium }

  - id: choose-spanish
    pressDtmf: { digits: "2", signaling: rfc2833, postDelayMs: 500 }

  - id: spanish-menu
    expectPrompt:
      timeoutSec: 12
      language: es-ES
      match:
        all:
          - regex: "facturaci(ó|o)n"
          - semantic: { intent: "Spanish billing menu offered" }

  - id: ask-balance
    pressDtmf: { digits: "1" }

  - id: account-prompt
    expectPrompt:
      timeoutSec: 10
      match: { substring: "account number" }

  - id: speak-account
    saySpeech: { text: "My account number is 1 2 3 4 5", voice: Joanna }

  - id: confirm-transfer
    expectTransfer:
      timeoutSec: 30
      to: { queueName: "Billing-ES" }
      allowPromptTextEvidence: true

  - id: end
    hangup: {}

postRun:
  fetchCcaasLogs: true
  correlationStrategy:
    ani: "{{ target.fromDid }}"
    timeWindowSec: 300
`;
